/*
 * File: WeSpeakTermsOfUse.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 9, 2025 at 5:09 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 10:38 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import {
	Meta,
	AppNavigation,
	LegalNavigation,
	Typography,
	LocaleLink,
} from '../../../../../src/components';
import { HighlightSection } from '../../../../../src/components/LegalNavigation/LegalNavigation.styles';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../../constants/base.constants';
import { LEGAL_NAVIGATION_SECTIONS } from '../../Constants';

// Styles
import * as S from './WeSpeakTermsOfUse.styles';


/**
 * Component
 */

const WeSpeakTermsOfUse = ({ meta: m }) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageWeSpeakTermsOfUse.metaTitle', { partnerName: partner.name }),
		description: t('pageWeSpeakTermsOfUse.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create navigation sections
	const navigationSections = useMemo(() => LEGAL_NAVIGATION_SECTIONS(locale), [locale]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Component Content */}
			<AppNavigation showFooter contentPadding={false}>
				<LegalNavigation
					navigationSections={navigationSections}
					mobileNavigationHeight={440}
				>
					<S.Wrapper>

						{/* Title */}
						<Typography tag="h1" weight="bold">
							We-Speak Terms of Service
						</Typography>

						{/* TL;DR */}
						<HighlightSection>
							<Typography tag="p">
								The We-Speak platform is powered by
								{' '}
								<LocaleLink
									title="Our Wave"
									aria-label="Our Wave"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									Our Wave
								</LocaleLink>
								, a nonprofit
								organization dedicated to building meaningful technology to empower survivors of sexual violence. For more
								information about our technology, team, or partnership opportunities, please
								{' '}
								<LocaleLink
									title="Visit Our Website"
									aria-label="Visit Our Website"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									visit our website
								</LocaleLink>
								.
							</Typography>
						</HighlightSection>

						{/* 1. INTRODUCTION */}
						<Typography tag="h2" weight="bold">1. INTRODUCTION</Typography>
						<div className="indent-container">
							<Typography tag="p">
								<b>1.1</b>
								{' '}
								The following terms and conditions apply to your use of this Dublin Rape Crisis Centre website
								(the &quot;
								<b>Website</b>
								&quot;) and any related services provided by Dublin Rape Crisis Centre in connection with the
								Website (&quot;
								<b>Services</b>
								&quot;) (the &quot;
								<b>Terms</b>
								&quot;). For the purpose of this document, &quot;use&quot; includes visiting
								the Website, browsing any information or material which has been made available on the Website (&quot;
								<b>Content</b>
								&quot;),
								uploading Content, or registering to use any services.
							</Typography>
							<Typography tag="p">
								<b>1.2</b>
								{' '}
								The Website is operated by Dublin Rape Crisis Centre (&quot;
								<b>DRCC</b>
								&quot; / &quot;
								<b>we</b>
								&quot; / &quot;
								<b>our</b>
								&quot; /
								&quot;
								<b>us</b>
								&quot;) and these Terms set out the rights and obligations of all users (&quot;
								<b>you</b>
								&quot; / &quot;
								<b>your</b>
								&quot; /
								&quot;
								<b>User</b>
								&quot;) in relation to your use of the Website.
							</Typography>
							<Typography tag="p">
								<b>1.3</b>
								{' '}
								By using the Website, you agree to the following Terms. If you do not agree to these Terms,
								please refrain from using the Website.
							</Typography>
						</div>
						<br />

						{/* 2. ACCEPTABLE USE */}
						<Typography tag="h2" weight="bold">2. ACCEPTABLE USE</Typography>
						<Typography tag="p">
							<b>2.1 Responsibility for information uploaded.</b>
							{' '}
							You accept sole responsibility for any information that you upload, share, exchange or display on the Website,
							including for the accuracy of the information uploaded.
						</Typography>
						<Typography tag="p">
							<b>2.2 Prohibited uses.</b>
							{' '}
							You must not use the Website or Services for any illegal or unauthorised purpose. Users residing outside of
							Ireland agree to comply with all local laws regarding acceptable online conduct. You must not engage in any
							attempt to modify, adapt, copy, or hack the Website. This includes falsely implying that another service or
							Third Party is affiliated with the Website. You must not crawl, scrape, or otherwise cache any content from the
							Website, including (but not limited to) personal details, photos, graphics, scripts or other content. You must
							not intentionally upload or transmit any viruses, malware, trojans, worms, spyware, adware, or any other malicious,
							destructive, or unauthorised software to the Website.
						</Typography>
						<Typography tag="p">
							<b>2.3 Our Wave.</b>
							{' '}
							When you submit information to the Website, you may be asked to share your story with our NGO partner
							{' '}
							<LocaleLink title="Our Wave" aria-label="Our Wave" target="_blank" to="https://www.ourwave.org" rel="noreferrer">Our Wave</LocaleLink>
							. Our Wave is non-profit organisation dedicated
							to building meaningful technology to empower survivors of sexual violence. If you chose to share your information
							with Our Wave, this will be governed by Our Wave&apos;s
							{' '}
							<LocaleLink title="Terms of Service" aria-label="Terms of Service" target="_blank" to="https://www.ourwave.org/terms" rel="noreferrer">Terms of Service</LocaleLink>
							{' '}
							and
							{' '}
							<LocaleLink title="Privacy Policy" aria-label="Privacy Policy" target="_blank" to="https://www.ourwave.org/privacy" rel="noreferrer">Privacy Policy</LocaleLink>
							.
						</Typography>
						<br />

						{/* Warranty */}
						<Typography tag="h2" weight="bold">3. WARRANTY</Typography>
						<Typography tag="p">
							You warrant that any contribution to the website complies with the acceptable use standards set out in this
							section 2 (acceptable use) of the terms, and you will be liable to us and indemnify us for any breach of that
							warranty. This means you will be responsible for any loss or damage we suffer as a result of your breach of warranty.
						</Typography>
						<br />

						{/* 4. USER CONTENT */}
						<Typography tag="h2" weight="bold">4. USER CONTENT</Typography>
						<Typography tag="p">
							<b>4.1</b>
							{' '}
							DRCC reserves the right to remove any User Content OR User Content which violates the Community Guidelines. We reserve
							the right to take similar action if we determine that the User Content infringes the rights and freedoms of others,
							or violates the terms set out in these Terms. Such actions will be undertaken at our sole discretion, but we assume
							no obligation to do so in every circumstance.
						</Typography>
						<Typography tag="p">
							<b>4.2</b>
							{' '}
							You may not sell, copy, reproduce, distribute, modify or otherwise exploit intellectual property owned by DRCC. We
							are the owner or the licensee of all intellectual property rights in the Website (except where specifically identified
							as third party content), and in the material published on it.  The DRCC intellectual property includes, but is
							not limited to, graphics, design, branding, computer code and interactive features.
						</Typography>
						<Typography tag="p">
							<b>4.3</b>
							{' '}
							Except for the making of a hard copy print for your own personal use only or the use of others in your organisation
							(or downloading the material for your own personal use or the use of others in your organisation only provided that
							you retain all copyright and proprietary notices), the materials on this Website may not be copied, reproduced,
							transmitted, distributed or displayed, by any means, without the express written consent of the Company.
						</Typography>
						<Typography tag="p">
							<b>4.4</b>
							{' '}
							You must not use any part of the materials on the Website for commercial purposes without obtaining a licence to do
							so from us or our licensors.
						</Typography>
						<br />

						{/* 5. PRIVACY AND DATA PROTECTION */}
						<Typography tag="h2" weight="bold">5. PRIVACY AND DATA PROTECTION</Typography>
						<Typography tag="p">
							<b>5.1</b>
							{' '}
							Any personal data which you provide in connection with your use of the Website or Services will be processed in
							accordance with our
							{' '}
							<LocaleLink title="Privacy Policy" aria-label="Privacy Policy" to="/legal/privacy">Privacy Policy</LocaleLink>
							.
						</Typography>
						<br />

						{/* 6. LIABILITY AND DISCLAIMER */}
						<Typography tag="h2" weight="bold">6. LIABILITY AND DISCLAIMER</Typography>
						<Typography tag="p">
							<b>6.1</b>
							{' '}
							To the extent permitted by law, DRCC excludes all express or implied conditions, warranties, representations, or
							other terms which may apply to the content and services provided on the website.
						</Typography>
						<Typography tag="p">
							<b>6.2</b>
							{' '}
							DRCC will not be held liable to any user for loss or damage arising from the use of our website or services,
							whether arising in contract, tort, statutory duty, or otherwise.
						</Typography>
						<Typography tag="p">
							<b>6.3</b>
							{' '}
							DRCC relies on crowd sourced content to provide certain information. We do not guarantee the accuracy of the
							information on the Website, and disclaim all liability for errors or inaccuracies in the information provided.
						</Typography>
						<Typography tag="p">
							<b>6.4</b>
							{' '}
							In cases where links to other websites, services or resources are provided on our Website, DRCC assumes no
							responsibility for the contents of these third party offerings. Such links are provided for your information
							only, and your use or reliance on the third party information is at your own risk.
						</Typography>
						<Typography tag="p">
							<b>6.5</b>
							{' '}
							Any services provided by a third party in connection with the Website may be subject to additional terms of
							service. It is your responsibility to review and familiarise yourself with these terms.
						</Typography>
						<br />

						{/* 7. WEBSITE AVAILABILITY */}
						<Typography tag="h2" weight="bold">7. WEBSITE AVAILABILITY</Typography>
						<Typography tag="p">
							<b>7.1</b>
							{' '}
							The Website is provided on an &quot;as available&quot; basis. Although we will make every reasonable effort to ensure
							continued uninterrupted access, there may be occasions where such access will be unavailable.
						</Typography>
						<br />

						{/* 8. VARIATIONS TO THE WEBSITE AND TERMS OF USE */}
						<Typography tag="h2" weight="bold">8. VARIATIONS TO THE WEBSITE AND TERMS OF USE</Typography>
						<Typography tag="p">
							<b>8.1</b>
							{' '}
							DRCC reserves the right to modify the Website, Content, and Services at any time without the requirement to
							provide notice.
						</Typography>
						<Typography tag="p">
							<b>8.2</b>
							{' '}
							If we make changes to these Terms or the
							{' '}
							<LocaleLink title="Privacy Policy" aria-label="Privacy Policy" to="/legal/privacy">Privacy Policy</LocaleLink>
							,
							we will notify you by providing appropriate notice on our Website. The date on which these Terms were last updated
							will also be indicated at the top of this webpage. Once you have received notification of such changes, your
							continued use of our Website and Services will indicate your awareness and acceptance of those changes.
						</Typography>
						<Typography tag="p">
							<b>8.3</b>
							{' '}
							If you do not agree to be bound by any changes to this Terms or to the
							{' '}
							<LocaleLink title="Privacy Policy" aria-label="Privacy Policy" to="/legal/privacy">Privacy Policy</LocaleLink>
							, you will not be able to continue to use
							our Website and Services.
						</Typography>
						<br />

						{/* 9. GOVERNING LAW, JURISDICTION AND VENUE */}
						<Typography tag="h2" weight="bold">9. GOVERNING LAW, JURISDICTION AND VENUE</Typography>
						<Typography tag="p">
							<b>9.1</b>
							{' '}
							The Irish courts will have exclusive jurisdiction over any claim arising from, or related to, a visit to the
							Website although we retain the right to bring proceedings against you for breach of these conditions in your
							country of residence or any other relevant country.
						</Typography>
						<Typography tag="p">
							<b>9.2</b>
							{' '}
							These Terms and any dispute or claim arising out of or in connection with them or their subject matter or
							formation (including non-contractual disputes or claims) shall be governed by and construed in accordance
							with the law of Ireland.
						</Typography>
						<br />

						{/* 10. TERMINATION */}
						<Typography tag="h2" weight="bold">10. TERMINATION</Typography>
						<Typography tag="p">
							<b>10.1</b>
							{' '}
							DRCC reserves the right to restrict, remove, or prevent access to our Website and Services if you fail to comply
							with these Terms. Such actions are at our sole discretion.
						</Typography>
						<Typography tag="p">
							<b>10.2</b>
							{' '}
							If any of these Terms are determined by any competent authority to be invalid, unlawful or unenforceable to any
							extent, such term, condition or provision will to the extent be severed from the remaining terms, conditions
							and provisions which will continue to be valid to the fullest extent permitted by law.
						</Typography>
						<br />

						{/* 11. COMPLAINTS */}
						<Typography tag="h2" weight="bold">11. COMPLAINTS</Typography>
						<Typography tag="p">
							<b>11.1</b>
							{' '}
							Should you have any complaints regarding the Website, Services, or these Terms, please direct these to the
							following email address:
							{' '}
							<LocaleLink title="info@we-consent.ie" aria-label="info@we-consent.ie" to="mailto:info@we-consent.ie?Subject=Request">info@we-consent.ie</LocaleLink>
							.
						</Typography>
						<Typography tag="p">
							<b>11.2</b>
							{' '}
							If you have any concerns, queries, or complaints regarding your data privacy, please refer to the relevant
							section in our
							{' '}
							<LocaleLink title="Privacy Policy" aria-label="Privacy Policy" to="/legal/privacy">Privacy Policy</LocaleLink>
							.
						</Typography>
						<br />

						{/* Updated Date */}
						<Typography tag="p">
							<i>Updated May 31, 2025</i>
						</Typography>

					</S.Wrapper>
				</LegalNavigation>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

WeSpeakTermsOfUse.propTypes = {
	meta: PropTypes.shape()
};
WeSpeakTermsOfUse.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default WeSpeakTermsOfUse;
